import moment from 'moment'
import type {
  SearchConditionErrors,
  SearchConditionsDraft,
  SearchConditionsValidationResult,
  ValidatedSearchConditions,
} from '../types'

type BenchmarkError = NonNullable<
  SearchConditionErrors['rows'][string]['benchmarkInput']
>

const parseBenchmarkInput = (input: string): number | BenchmarkError => {
  const trimmed = input.trim()
  if (!trimmed) return 'required'
  if (!/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(trimmed)) return 'invalid'

  const benchmark = Number(trimmed)
  if (!Number.isFinite(benchmark)) return 'invalid'
  if (benchmark < 0 || benchmark > 100) return 'range'
  if ((trimmed.split('.')[1]?.length ?? 0) > 4) return 'precision'

  return benchmark
}

export const validateSearchConditions = (
  draft: SearchConditionsDraft
): SearchConditionsValidationResult => {
  const errors: SearchConditionErrors = { rows: {} }
  if (!draft.baseDate.trim()) {
    errors.baseDate = 'required'
  } else if (
    !/^\d{4}-\d{2}-\d{2}$/.test(draft.baseDate) ||
    !moment(draft.baseDate, 'YYYY-MM-DD', true).isValid()
  ) {
    errors.baseDate = 'invalid'
  }

  const portfolioCounts = new Map<number, number>()
  for (const row of draft.rows) {
    for (const portfolioId of row.portfolioIds) {
      portfolioCounts.set(
        portfolioId,
        (portfolioCounts.get(portfolioId) ?? 0) + 1
      )
    }
  }
  if (portfolioCounts.size === 0) errors.portfolios = 'required'

  const rows: ValidatedSearchConditions['rows'] = []
  for (const row of draft.rows) {
    if (row.portfolioIds.length === 0) continue

    const rowErrors: SearchConditionErrors['rows'][string] = {}
    if (
      row.portfolioIds.some(
        (portfolioId) => (portfolioCounts.get(portfolioId) ?? 0) > 1
      )
    ) {
      rowErrors.portfolioIds = 'duplicate'
    }

    const benchmark = parseBenchmarkInput(row.benchmarkInput)
    if (typeof benchmark === 'number') {
      rows.push({ portfolioIds: [...row.portfolioIds], benchmark })
    } else {
      rowErrors.benchmarkInput = benchmark
    }
    if (Object.keys(rowErrors).length > 0) {
      errors.rows = { ...errors.rows, [row.rowId]: rowErrors }
    }
  }

  if (errors.baseDate || errors.portfolios || Object.keys(errors.rows).length) {
    return { isValid: false, errors }
  }

  return {
    isValid: true,
    value: {
      baseDate: draft.baseDate,
      rows,
      groupBy: draft.groupBy,
      isAppliedAccountMTD: draft.isAppliedAccountMTD,
      useFullFundName: draft.useFullFundName,
    },
  }
}
